'use client'

import React, { useMemo, useCallback, memo } from 'react'
import Image from 'next/image'
import SmallGirlImage from '../../../public/images/little-girl.png';
import SmallBoyImage from '../../../public/images/little-boy.png';
import MobileProductSlider from '../Products/MobileProductSlider';
import MobileProductOptions from '../Products/MobileProductOptions';
import ProductDisclosure from '../Products/ProductFAQDisclosure';
import NewProductItem from '../Products/NewProductItem';
import Button from '../Button';
import StarRatings from 'react-star-ratings'
import { ReviewStar, StarIcon, EditReviewIcon, DeleteReviewIcon } from '@/utils/svgIcons';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

interface MobileProductSectionProps {
  product: any
  name: string
  gender: string
  productGender: 'Male' | 'Female'
  averageReviewStars: number
  check_in_wishlist: any
  WishlistDel: any
  addToWishlist: any
  handlePersonalizeClick: () => void
  settings: any
  reviews: any[]
  user: any
  reviewStar: number
  setReviewStar: (rating: number) => void
  review: string
  setReview: (text: string) => void
  wordCount: number
  isEditing: any
  setIsEditing: (val: any) => void
  handleCreate: () => void
  handleUpdate: () => void
  mutation: any
  UpdateMutation: any
  DeleteMutation: any
  editReview: (id: number, stars: number, review: string) => void
  deleteReview: (id: number) => void
  related_products: any[]
  scrollRef: React.RefObject<HTMLDivElement>
  ctaRef: React.RefObject<HTMLDivElement>
}

// Memoized star rating component
const StarRating = memo(({ averageReviewStars }: { averageReviewStars: number }) => {
  const starsToShow = useMemo(() => {
    return (averageReviewStars === 0 || averageReviewStars === undefined || Number.isNaN(averageReviewStars)) ? 4 : averageReviewStars;
  }, [averageReviewStars]);

  return (
    <div className="flex gap-1 mt-2">
      {[...Array(5)].map((_, i) => (
        <span key={i} className={`w-6 h-6 ${i < starsToShow ? 'text-yellow-500' : 'text-gray-500'}`}>
          <StarIcon />
        </span>
      ))}
    </div>
  );
});
StarRating.displayName = 'StarRating';

// Memoized product header component
const ProductHeader = memo(({ 
  DemoName, 
  name, 
  product, 
  locale, 
  productGender,
  hasSale,
  discountPercentage,
  t 
}: any) => {
  const displayName = useMemo(() => 
    name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en),
    [name, locale, product?.replace_name]
  );

  const discountText = useMemo(() => {
    if (!hasSale) return null;
    return locale === 'ar' 
      ? `${t('discount')} %${discountPercentage}`
      : `${discountPercentage}% ${t('discount')}`;
  }, [hasSale, discountPercentage, locale, t]);

  return (
    <div className={`flex items-start justify-between w-full gap-6`} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className={`flex items-start justify-between min-w-0 gap-2`}>
        <h2 className="text-2xl font-normal text-start break-words leading-relaxed">
          {(DemoName || '').split('{basmti}')[0]}
          <span className="text-success-500 font-bold">{displayName}</span>
          {(DemoName || '').split('{basmti}')[1]}
        </h2>
        <span className="w-8 h-8 shrink-0">
          <Image
            src={productGender === 'Female' ? SmallGirlImage : SmallBoyImage}
            className="w-full"
            alt={productGender === 'Female' ? "small-girl" : "small-boy"}
            loading="lazy"
          />
        </span>
      </div>

      {hasSale && (
        <div className="bg-red-500 text-white text-sm px-4 py-1 rounded-md font-bold shrink-0 mr-4">
          <p className={locale === 'ar' ? 'block' : 'hidden'}>{discountText}</p>
          <p className={locale === 'en' ? 'block' : 'hidden'}>{discountText}</p>
        </div>
      )}
    </div>
  );
});
ProductHeader.displayName = 'ProductHeader';

// Memoized review item component
const ReviewItem = memo(({ 
  review, 
  user, 
  editReview, 
  deleteReview 
}: { 
  review: any; 
  user: any; 
  editReview: (id: number, stars: number, review: string) => void;
  deleteReview: (id: number) => void;
}) => {
  const isUserReview = user?.id === review.user_id;
  
  return (
    <div className={`${isUserReview ? 'bg-gray-100' : ''} flex flex-col items-start justify-between px-4 py-2`}>
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <ReviewStar key={i} filled={review.stars > i} />
        ))}
      </div>
      <h2 className="font-semibold text-primary mt-1">{review.name}</h2>
      <div className="review-text text-text-color mt-4">{review.review}</div>
      {isUserReview && (
        <div className="review-actions flex gap-4 items-center justify-center mt-4">
          <button
            className="bg-success p-2 rounded-lg"
            onClick={() => editReview(review.id, review.stars, review.review)}
            aria-label="Edit review"
          >
            <EditReviewIcon />
          </button>
          <button
            className="bg-red p-2 rounded-lg"
            onClick={() => deleteReview(review.id)}
            aria-label="Delete review"
          >
            <DeleteReviewIcon />
          </button>
        </div>
      )}
      <hr className="h-[1px] bg-[#FEF4C1] w-full mt-8" />
    </div>
  );
});
ReviewItem.displayName = 'ReviewItem';

// Memoized review form component
const ReviewForm = memo(({
  reviewStar,
  setReviewStar,
  review,
  setReview,
  wordCount,
  isEditing,
  setIsEditing,
  handleCreate,
  handleUpdate,
  mutation,
  UpdateMutation,
  DeleteMutation,
  t,
  locale
}: any) => {
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing) handleCreate();
    else handleUpdate();
  }, [isEditing, handleCreate, handleUpdate]);

  const handleCancelEdit = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setReview('');
    setReviewStar(0);
    setIsEditing(undefined);
  }, [setReview, setReviewStar, setIsEditing]);

  const handleRatingChange = useCallback((newRating: number) => {
    setReviewStar(newRating);
  }, [setReviewStar]);

  const isLoading = mutation.isLoading || UpdateMutation.isLoading || DeleteMutation.isLoading;

  return (
    <div className="container add-review mt-10" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <h1 className="text-lg font-medium">{t('reviewFormHeading')}</h1>
      <p className="text-sm font-semibold mt-4">{t('reviewFormSubHeading')}</p>
      <div className="ratings-star mt-1">
        <StarRatings
          rating={reviewStar}
          starRatedColor="#FBDD37"
          starHoverColor="#FBDD37"
          starDimension="21px"
          starSpacing="2"
          changeRating={handleRatingChange}
          numberOfStars={5}
          name="rating"
        />
      </div>
      <form onSubmit={handleSubmit}>
        <textarea
          className="rounded-md border border-[#93A3B03D] w-full mt-5 p-4 pb-6 text-sm placeholder-[#93A3B0CC] placeholder:font-light"
          rows={5}
          value={review}
          onChange={e => setReview(e.target.value)}
          placeholder={t('textAreaPlaceholder')}
        />
        <div className="flex items-center">
          <div className="text-[#93A3B0CC] text-sm font-light">
            {280 - wordCount} {t('wordLimit')}
          </div>
          {isEditing && (
            <button
              onClick={handleCancelEdit}
              className="text-sm mr-2 text-gray-400"
            >
              {t('editReviewButton')}
            </button>
          )}
        </div>
        <div className="btn lg:max-w-sm lg:mx-auto">
          <Button
            type="submit"
            bg="primary"
            style="outline"
            isLoading={isLoading}
            classes={[
              'mt-6',
              'py-4',
              'bg-transparent',
              'rounded-md',
              'w-full',
              'border-[1px]',
              'border-primary-500',
              'text-primary-500',
              'text-base',
              'font-bold'
            ]}
          >
            {t('reviewSubmit')}
          </Button>
        </div>
      </form>
    </div>
  );
});
ReviewForm.displayName = 'ReviewForm';

// Memoized related products component - FIXED: move useMemo before conditional return
const RelatedProducts = memo(({ 
  related_products, 
  scrollRef, 
  dotCount, 
  activeDot, 
  gender, 
  name,
  t,
  locale 
}: any) => {
  // Move useMemo to top, before any conditional returns
  const productsList = useMemo(() => 
    related_products?.map((related: any, index: number) => (
      <NewProductItem
        key={`${related.id}-${index}`}
        product={related}
        gender={gender}
        name={name}
      />
    )) ?? [],
    [related_products, gender, name]
  );

  if (!related_products?.length) return null;

  return (
    <div className="px-4 mt-12 lg:mt-24" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <h2 className="text-black font-medium text-lg">
        {t('relatedProducts')}
      </h2>
      <div 
        ref={scrollRef}
        className="products-list mt-6 flex flex-row items-center justify-start gap-3 md:gap-6 overflow-x-auto overflow-y-hidden scrollbar-hide snap-x snap-mandatory"
      >
        {productsList}
      </div>
      {dotCount > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: dotCount }).map((_, i) => (
            <span
              key={i}
              className={`h-2 w-2 rounded-full transition-all duration-300 ${
                i === activeDot ? 'bg-primary-500 w-4' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
});
RelatedProducts.displayName = 'RelatedProducts';

// Hook for scroll dots
const useScrollDots = (scrollRef: React.RefObject<HTMLDivElement>, locale: string, related_products: any[]) => {
  const [activeDot, setActiveDot] = React.useState(0);
  const [dotCount, setDotCount] = React.useState(0);

  const getNormalizedScrollLeft = useCallback((el: HTMLDivElement, isRTL: boolean) => {
    if (!isRTL) return el.scrollLeft;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    if (el.scrollLeft < 0) return Math.abs(el.scrollLeft);
    return maxScrollLeft - el.scrollLeft;
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const isRTL = locale === 'ar';

    const updateDots = () => {
      const containerWidth = el.clientWidth;
      const scrollWidth = el.scrollWidth;
      const maxScroll = scrollWidth - containerWidth;

      if (maxScroll <= 0) {
        setDotCount(1);
        setActiveDot(0);
        return;
      }

      const totalDots = Math.ceil(scrollWidth / containerWidth);
      setDotCount(totalDots);

      const normalizedScrollLeft = getNormalizedScrollLeft(el, isRTL);
      const progress = normalizedScrollLeft / maxScroll;
      const index = Math.round(progress * (totalDots - 1));
      setActiveDot(Math.min(Math.max(index, 0), totalDots - 1));
    };

    updateDots();
    el.addEventListener('scroll', updateDots, { passive: true });
    window.addEventListener('resize', updateDots);

    return () => {
      el.removeEventListener('scroll', updateDots);
      window.removeEventListener('resize', updateDots);
    };
  }, [related_products, locale, scrollRef, getNormalizedScrollLeft]);

  return { activeDot, dotCount };
};

// Memoized FAQ section
const FAQSection = memo(({ FAQS }: { FAQS: any[] }) => {
  if (!FAQS?.length) return null;
  
  return (
    <div className="container mx-auto faq lg:mt-8">
      {FAQS.map((faq: any, index: number) => (
        <ProductDisclosure key={`${faq.id || index}`} faq={faq} />
      ))}
    </div>
  );
});
FAQSection.displayName = 'FAQSection';

// Memoized reviews section - FIXED: move useMemo before conditional return
const ReviewsSection = memo(({ reviews, user, editReview, deleteReview }: any) => {
  // Move useMemo to top
  const reviewsList = useMemo(() => 
    reviews?.map((item: any, index: number) => (
      <ReviewItem
        key={`${item.id}-${index}`}
        review={item}
        user={user}
        editReview={editReview}
        deleteReview={deleteReview}
      />
    )) ?? [],
    [reviews, user, editReview, deleteReview]
  );

  if (!reviews?.length) return null;

  return (
    <div className="flex flex-col gap-4 mt-8">
      {reviewsList}
    </div>
  );
});
ReviewsSection.displayName = 'ReviewsSection';

const MobileProductSection = ({
  product,
  name,
  gender,
  productGender,
  averageReviewStars,
  check_in_wishlist,
  WishlistDel,
  addToWishlist,
  handlePersonalizeClick,
  settings,
  reviews,
  user,
  reviewStar,
  setReviewStar,
  review,
  setReview,
  wordCount,
  isEditing,
  setIsEditing,
  handleCreate,
  handleUpdate,
  mutation,
  UpdateMutation,
  DeleteMutation,
  editReview,
  deleteReview,
  related_products,
  scrollRef,
  ctaRef
}: MobileProductSectionProps) => {
  const { t } = useTranslation('product');
  const { locale } = useRouter();
  
  // Get scroll dots
  const { activeDot, dotCount } = useScrollDots(scrollRef, locale === 'ar' ? 'ar' : 'en', related_products);

  // Memoize derived values
  const displayPrice = useMemo(() => product.price, [product.price]);
  const lang = useMemo(() => locale === 'ar' ? 'ar' : 'en', [locale]);
  const FAQS = useMemo(() => {
    return Array.isArray(settings?.faqs?.[lang]) ? settings.faqs[lang] : [];
  }, [settings?.faqs, lang]);
  
  const DemoName = useMemo(() => {
    return product?.demo_name 
      ? (locale === 'ar' 
          ? product.demo_name?.ar || product.demo_name?.en || ''
          : product.demo_name?.en || product.demo_name?.ar || '')
      : '';
  }, [locale, product?.demo_name]);

  const productDescription = useMemo(() => {
    return locale === 'ar' 
      ? product.description?.ar || '' 
      : product.description?.en || '';
  }, [locale, product.description]);

  const excerpt = useMemo(() => {
    if (product?.excerpt) {
      return locale === 'ar' 
        ? product.excerpt?.ar || product.excerpt?.en || ''
        : product.excerpt?.en || product.excerpt?.ar || '';
    }
    return ' قصتنا الأكثر مبيعًا لمساعدة الطفل على التعرف على وسائل النقل';
  }, [locale, product?.excerpt]);

  const hasSale = useMemo(() => product?.has_sale, [product?.has_sale]);
  const discountPercentage = useMemo(() => product?.discount_percentage, [product?.discount_percentage]);

  return (
    <div className='block lg:hidden bg-white'>
      {/* Product header section */}
      <div className="container p-4 flex flex-col items-start text-right gap-1" ref={ctaRef} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <ProductHeader
          DemoName={DemoName}
          name={name}
          product={product}
          locale={locale}
          productGender={productGender}
          hasSale={hasSale}
          discountPercentage={discountPercentage}
          t={t}
        />

        {/* Product rating */}
        <StarRating averageReviewStars={averageReviewStars} />

        {/* Product excerpt */}
        <p className={`text-[#272727] mt-1 ${locale === 'ar' ? 'text-right' : 'text-left'}`} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
          {excerpt}
        </p>
      </div>

      {/* Product gallery */}
      <MobileProductSlider images={product?.images} />

      {/* Product options */}
      <MobileProductOptions 
        demoProductDescription={productDescription} 
        name={name} 
        product={product}
        check_in_wishlist={check_in_wishlist}
        WishlistDel={WishlistDel}
        addToWishlist={addToWishlist}
        setPersonalizeModal={handlePersonalizeClick}
      />

      {/* Separator */}
      <hr className='h-[2px] mx-4 my-8 bg-[#E6E9ED] rounded-sm' />

      {/* FAQ Section */}
      <FAQSection FAQS={FAQS} />

      {/* Reviews Section */}
      <ReviewsSection 
        reviews={reviews}
        user={user}
        editReview={editReview}
        deleteReview={deleteReview}
      />

      {/* Review Form */}
      {user && (
        <ReviewForm
          reviewStar={reviewStar}
          setReviewStar={setReviewStar}
          review={review}
          setReview={setReview}
          wordCount={wordCount}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCreate={handleCreate}
          handleUpdate={handleUpdate}
          mutation={mutation}
          UpdateMutation={UpdateMutation}
          DeleteMutation={DeleteMutation}
          t={t}
          locale={locale}
        />
      )}

      {/* Related Products */}
      <RelatedProducts
        related_products={related_products}
        scrollRef={scrollRef}
        dotCount={dotCount}
        activeDot={activeDot}
        gender={gender}
        name={name}
        t={t}
        locale={locale}
      />
    </div>
  );
};

export default memo(MobileProductSection);